import type { FC } from 'react'
import { useMemo } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Tooltip, useTheme } from '@mui/material'
import SquareOutlined from '@mui/icons-material/SquareOutlined'
import { keyframes } from '@mui/system'
import { standardTooltipProps } from '@/common/components'
import type { ClaudeSession } from '@/features/events/conversation/state/types'
import {
  deriveSessionAgentModeState,
  type AgentModeTransition,
} from '@/features/events/shared/utils/agentModeUtils'
import {
  AgentMode,
  resolveAgentModeBadgeColor,
  resolveAgentModeBadgeLabel,
} from '@/features/events/shared/types/enums'

interface AgentModeSessionIconProps {
  readonly session: ClaudeSession
  readonly size?: string
}

export const AgentModeSessionIcon: FC<AgentModeSessionIconProps> = ({
  session,
  size = '0.875rem',
}) => {
  const agentModeState = useMemo(() => deriveSessionAgentModeState(session), [session])

  return (
    <AgentModeIcon
      agentMode={agentModeState.currentMode}
      pendingTransition={agentModeState.pendingTransition}
      size={size}
    />
  )
}

interface AgentModeIconProps {
  readonly agentMode: AgentMode
  readonly size?: string
  readonly pendingTransition?: AgentModeTransition | null
}

const AgentModeIcon: FC<AgentModeIconProps> = ({
  agentMode,
  size = '0.875rem',
  pendingTransition,
}) => {
  const theme = useTheme()
  const paletteMode = theme.palette.mode
  const colors = resolveAgentModeBadgeColor(agentMode, paletteMode)
  const label = resolveAgentModeBadgeLabel(agentMode)

  const tooltipLabel = pendingTransition
    ? `${resolveAgentModeBadgeLabel(pendingTransition.from)} → ${resolveAgentModeBadgeLabel(pendingTransition.to)}`
    : label

  const transitionAnimation = useMemo(() => {
    if (!pendingTransition) return null

    const fromColor = resolveAgentModeBadgeColor(pendingTransition.from, paletteMode).bg
    const toColor = resolveAgentModeBadgeColor(pendingTransition.to, paletteMode).bg

    return keyframes`
            0%, 100% {
                color: ${fromColor};
            }
            50% {
                color: ${toColor};
            }
        `
  }, [pendingTransition])

  if (pendingTransition && transitionAnimation) {
    return (
      <Tooltip {...standardTooltipProps} title={tooltipLabel}>
        <Box sx={iconWrapperSx}>
          <SquareOutlined sx={createAnimatedIconSx(transitionAnimation, size)} />
        </Box>
      </Tooltip>
    )
  }

  return (
    <Tooltip {...standardTooltipProps} title={tooltipLabel}>
      <Box sx={iconWrapperSx}>
        <SquareOutlined sx={staticIconSx(colors.bg, size)} />
      </Box>
    </Tooltip>
  )
}

const iconWrapperSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const staticIconSx = (color: string, size: string): SxProps<Theme> => ({
  color,
  fontSize: size,
  display: 'flex',
  transform: 'rotate(45deg)',
  opacity: 0.7,
})

function createAnimatedIconSx(
  animation: ReturnType<typeof keyframes>,
  size: string,
): SxProps<Theme> {
  return {
    fontSize: size,
    display: 'flex',
    transform: 'rotate(45deg)',
    animation: `${animation} 1.5s ease-in-out infinite`,
  }
}
